import { createContext } from 'preact';
import { useContext, useState } from 'preact/hooks';
import { ServiceContentById } from '@/api';
import { Service } from '@/base-api/types';

type Services = Service[];

interface ServiceStore {
  serviceContentById: ServiceContentById;
  variantId: number | null;
  services: Services | null;
  activeOfferingByServiceId: Record<number, number>;
  setServiceContentById: (serviceObj: ServiceContentById) => void;
  setVariantId: (id: number | null) => void;
  setServices: (offerings: Services | null) => void;
  setActiveOfferingByServiceId: (
    serviceId?: number,
    offeringId?: number | null,
  ) => void;
  hasActiveOfferings: boolean;
  hasServicesAvailable: boolean;
}

const ServiceContext = createContext<ServiceStore | undefined>(undefined);

export function useServiceStore() {
  const context = useContext(ServiceContext);
  if (!context) {
    throw new Error('useServiceStore must be used within a ServiceProvider');
  }
  return context;
}

export function ServiceProvider({
  children,
  serviceContentById,
}: {
  children: preact.ComponentChildren;
  serviceContentById: ServiceContentById;
}) {
  const [variantId, setVariantId] = useState<number | null>(null);
  const [services, setServices] = useState<Services | null>(null);
  const [serviceContentByIdState, setServiceContentById] =
    useState<ServiceContentById>(serviceContentById);
  const [activeOfferingByServiceId, setActiveOfferingByServiceIdState] =
    useState<Record<number, number>>({});

  const hasActiveOfferings = Object.keys(activeOfferingByServiceId).length > 0;

  const hasServicesAvailable = !!services && services.length > 0;

  const setActiveOfferingByServiceId = (
    serviceId?: number,
    offeringId?: number | null,
  ) => {
    setActiveOfferingByServiceIdState((prev) => {
      if (serviceId === undefined) {
        return {};
      }

      const newState = { ...prev };
      const isToggledOff = prev[serviceId] === offeringId;

      if (isToggledOff || offeringId === null || offeringId === undefined) {
        delete newState[serviceId];
      } else {
        newState[serviceId] = offeringId;
      }

      return newState;
    });
  };

  const store: ServiceStore = {
    serviceContentById: serviceContentByIdState,
    variantId,
    services,
    setServiceContentById,
    setVariantId,
    setServices,
    activeOfferingByServiceId,
    setActiveOfferingByServiceId,
    hasActiveOfferings,
    hasServicesAvailable,
  };

  window.itsServices = window.itsServices || undefined;

  if (window.itsServices) {
    window.itsServices = {
      ...window.itsServices,
      shouldBypassPreviewModal: () => {
        // if (hasActiveOfferings || (!hasActiveOfferings && hasServicesAvailable))
        //   return true;
        if (!hasActiveOfferings && hasServicesAvailable) return true;
        return false;
      },
      hasActiveOfferings: () => hasActiveOfferings,
      hasServicesAvailable: () => hasServicesAvailable,
    };
  }

  return (
    <ServiceContext.Provider value={store}>{children}</ServiceContext.Provider>
  );
}
